import { useMutation, useQuery } from "@apollo/client";
import Search from "@mui/icons-material/Search";
import { Alert, Box, InputAdornment, Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { Issue, IssueLevel } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useMemo, useState } from "react";
import { hasCriticalIssues } from "util/forms";

import { UploadableShippingKind } from "../Manifests/ShippingRecordUploadFlow";
import {
  ATTACH_SHIPMENT_MANIFEST_MUTATION,
  CANDIDATE_MANIFESTS_QUERY,
  MANIFEST_LINK_REFETCH_QUERIES,
  VALIDATE_ATTACH_SHIPMENT_MANIFEST_INPUT,
} from "./api";
import { ManifestPickerList } from "./ManifestPickerList";
import { UploadFallback } from "./UploadFallback";

/** How many manifests the list offers before asking the user to narrow it. */
const PAGE_SIZE = 25;

/**
 * Pick the manifest that came back for this shipment.
 *
 * Only already-ingested manifests can be attached — a freshly uploaded document
 * has to be parsed and pass through the pipeline before a `ProcessedManifest`
 * exists at all — so this is a search over what has landed, not an upload.
 *
 * Uploading is offered here rather than beside this on the card, because it is
 * not an alternative route to the same place: it starts a document through the
 * pipeline so it can be linked later. Which one applies depends on whether
 * Encamp already has the manifest, and this list is how the reader finds that
 * out — so the offer belongs at the point the search comes up short.
 */
export function AttachManifestDialog({
  shipmentId,
  facilityId,
  onClose,
  onUploadInstead,
  canUploadBillOfLading = false,
  replacingTrackingNumber = null,
}: {
  shipmentId: string;
  facilityId: string;
  onClose: () => void;
  /**
   * Hands off to the upload flow. Omitted when replacing an existing manifest:
   * that is a deliberate swap for one already received, not a search that can
   * come up empty.
   */
  onUploadInstead?: (kind: UploadableShippingKind) => void;
  /** Offers the bill of lading link beside the manifest one. */
  canUploadBillOfLading?: boolean;
  /**
   * The manifest currently linked, when this dialog was opened to replace it.
   * Linking is an upsert, so the swap is silent unless it is spelled out.
   */
  replacingTrackingNumber?: string | null;
}) {
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const [text, setText] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // `tenantId:` is REQUIRED, not just a narrowing: the processedManifests
  // resolver reads the tenant out of the search string and answers an empty page
  // when it is absent, and its authorizer scopes on the same term. Without it
  // this dialog reports "no manifests found" for every facility, however many
  // manifests it has actually received.
  //
  // The facility term is the narrowing part — attaching a manifest from another
  // facility is refused by the rules anyway, so offering one would only produce
  // a dead end.
  const search = useMemo(
    () =>
      [`tenantId:${tenantId}`, `facilityId:${facilityId}`, text.trim()].filter(Boolean).join(" "),
    [tenantId, facilityId, text]
  );

  const { data, loading } = useQuery(CANDIDATE_MANIFESTS_QUERY, {
    variables: {
      search,
      pageSize: PAGE_SIZE,
      sort: [{ field: "shippedDate", sort: "desc" }],
    },
    skip: !tenantId,
    fetchPolicy: "cache-and-network",
  });

  const manifests = data?.processedManifests?.items ?? [];
  const total = data?.processedManifests?.count ?? 0;

  // The selection is DERIVED from what the list currently holds, not just read
  // back from state. Narrowing the search can drop the chosen row out of the
  // list, and a remembered id would then stay submittable while invisible.
  // Deriving it means the selection simply stops existing when its row does,
  // with no effect to clear it and no window where the two disagree.
  const selected = manifests.find((manifest) => manifest.id === selectedId) ?? null;

  // Validate the chosen row before submitting, so "already attached to another
  // shipment" reads as an explanation under the selection rather than an error
  // after the fact.
  const { data: validation, loading: validating } = useQuery(
    VALIDATE_ATTACH_SHIPMENT_MANIFEST_INPUT,
    {
      variables: {
        input: { shipmentId, processedManifestId: selected?.id ?? "", signed: false },
      },
      skip: !selected,
      fetchPolicy: "network-only",
    }
  );
  const issues = selected ? (validation?.attachShipmentManifestInputValidator ?? []) : [];
  const blocked = hasCriticalIssues(issues);

  const [attach, { loading: attaching }] = useMutation(ATTACH_SHIPMENT_MANIFEST_MUTATION, {
    refetchQueries: MANIFEST_LINK_REFETCH_QUERIES,
  });

  const handleAttach = async () => {
    if (!selected || blocked) {
      return;
    }
    try {
      // Unsigned by default: the common case is the vendor's copy arriving
      // ahead of the signed original, and "Mark signed copy" under the manifest
      // step is how the signed copy is recorded later. Replacing therefore also
      // clears it, which is the way back from marking it by mistake.
      await attach({
        variables: { input: { shipmentId, processedManifestId: selected.id, signed: false } },
      });
      alerts.success("Manifest attached.");
      onClose();
    } catch (e) {
      alerts.error(e instanceof Error ? e.message : "Failed to attach the manifest.");
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={replacingTrackingNumber ? "Replace this manifest" : "Link a manifest"}
      primaryActionButtonProps={{
        saveText: replacingTrackingNumber ? "Replace manifest" : "Link manifest",
        loading: attaching,
        disabled: !selected || validating || blocked,
        onClick: handleAttach,
      }}
    >
      <Stack spacing={2}>
        <ReplacementWarning trackingNumber={replacingTrackingNumber} />
        <Typography variant="body2" color="text.secondary">
          Manifests already received for this facility. Search by tracking number.
        </Typography>
        <ManifestSearchField value={text} onChange={setText} />

        <ManifestPickerList
          manifests={manifests}
          total={total}
          loading={loading}
          searching={text.trim().length > 0}
          selectedId={selected?.id ?? null}
          onSelect={setSelectedId}
        />

        {onUploadInstead && (
          <UploadFallback
            onUpload={onUploadInstead}
            canUploadBillOfLading={canUploadBillOfLading}
          />
        )}

        <AttachIssueList issues={issues} />
      </Stack>
    </Dialog>
  );
}

/**
 * Linking is an upsert, so swapping one manifest for another is silent unless
 * it is spelled out.
 */
function ReplacementWarning({ trackingNumber }: { trackingNumber: string | null }) {
  if (!trackingNumber) {
    return null;
  }
  return (
    <Alert severity="warning">
      This shipment is already linked to {trackingNumber}. Linking another manifest releases that
      one and re-points this shipment&apos;s containers at the new manifest&apos;s waste lines.
    </Alert>
  );
}

/**
 * Why the chosen manifest cannot be attached, if it cannot. Rendered under the
 * selection rather than raised on submit, so the refusal reads as a property of
 * the row the user just clicked.
 */
function AttachIssueList({ issues }: { issues: readonly Issue[] }) {
  if (issues.length === 0) {
    return null;
  }
  return (
    <Box>
      {issues.map((issue, index) => (
        <Alert
          key={`${issue.message}-${index}`}
          severity={issue.level === IssueLevel.Critical ? "error" : "warning"}
          sx={{ mt: 1 }}
        >
          {issue.message}
        </Alert>
      ))}
    </Box>
  );
}

/** Tracking-number search for the picker. */
function ManifestSearchField({
  value,
  onChange,
}: {
  value: string;
  onChange: (next: string) => void;
}) {
  return (
    <TextField
      size="small"
      fullWidth
      autoFocus
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Tracking number"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        },
      }}
    />
  );
}
